'use client'

import LoadingSpinner from '@dom/LoadingSpinner'
import { produce } from 'immer'
import { memo, useEffect, useState, useMemo } from 'react'

/**
 * Custom hook to determine if the code is running on the client side after mounting.
 * This version aims for singleton behavior by using module-scoped variables
 * (_isClientSingletonState, _isClientSingletonSetters) that you must define.
 * @returns {boolean} True if on the client and mounted, otherwise false.
 */
// Assuming `produce` from `immer` is imported and available in this scope.
// e.g., import { produce } from 'immer';

const useIsClient = () => {
  // Initialize local state from the (intended) module-scoped singleton state.
  const [isClient, setIsClient] = useState(
    typeof _isClientSingletonState !== 'undefined' ? _isClientSingletonState : false
  )

  useEffect(() => {
    // Ensure module-scope variables are accessible; otherwise, this won't be a true singleton.
    if (typeof _isClientSingletonState === 'undefined' || typeof _isClientSingletonSetters === 'undefined') {
      // Fallback to component-local behavior if module-scope variables are not set up.
      // This makes the hook behave like the original non-singleton version in such a case.
      // Use immer's produce for the state update. For a boolean, the recipe returns the new value.
      setIsClient(prevState => produce(prevState, () => true))
      return
    }

    // Add this instance's state setter to the set of listeners.
    _isClientSingletonSetters.add(setIsClient)

    // If the shared client state is not yet true, this means we are on the client
    // (as useEffect runs client-side) and this is likely the first component instance
    // (or one of the first) to mount and run this effect.
    if (!_isClientSingletonState) {
      // _isClientSingletonState is a module-scoped primitive boolean.
      // `produce` is not used here as it's a direct mutation of a primitive module-scoped variable,
      // and `produce` is designed for objects, arrays, Maps, or Sets.
      _isClientSingletonState = true // Set the shared state to true.
      // Notify all subscribed components by calling their state setters.
      // Each setter will handle its state update; if other instances of useIsClient
      // are also modified to use produce, they will do so internally.
      _isClientSingletonSetters.forEach(setter => setter(true))
    } else {
      // If the shared state was already true (e.g., set by another instance),
      // ensure this instance's local state is also true.
      // This handles cases where useState might have initialized `isClient` to `false`
      // (if _isClientSingletonState was false at that time) but the shared state
      // became true before this effect ran or due to a re-render.
      if (!isClient) {
        // Use immer's produce for the state update.
        setIsClient(prevState => produce(prevState, draft => true))
      }
    }

    // Cleanup: remove the setter when the component unmounts.
    return () => {
      _isClientSingletonSetters.delete(setIsClient)
    }
  }, [isClient]) // isClient dependency helps ensure local state syncs if it ever diverges and then re-evaluates.
  // setIsClient from useState has a stable identity and is not required in the dependency array.

  return isClient
}

/**
 * Componente LoadingWrapper
 * @description Muestra un spinner de carga hasta que el entorno del cliente esté listo para renderizar el contenido.
 * @param {React.ReactNode} children - Contenido a renderizar una vez que el cliente esté listo.
 * @returns {JSX.Element} Contenedor con lógica de carga.
 */
export const LoadingWrapper = memo(({ children }) => {
  // IMPORTANT: For the 'useIsClient' hook to be a true singleton,
  // the following variables must be defined at the module scope (e.g., at the top of this file):
  // let _isClientSingletonState = false; // Stores the shared client status
  // const _isClientSingletonSetters = new Set(); // Stores setter functions from hook instances

  const isClient = useIsClient()

  const memoizedChildren = useMemo(() => children, [children])

  // Mostrar el spinner solo mientras no se haya confirmado el entorno del cliente.
  // Una vez que isClient es true, se renderizarán los children,
  // incluso si son null o undefined (React los manejará apropiadamente).
  if (!isClient) {
    return (
      <div
        id='init-page-with-spinner'
        role='status'
        aria-busy='true'
        aria-label='Cargando contenido'
        className='flex items-center justify-center h-screen'
      >
        <LoadingSpinner />
        <span className='sr-only'>⏳ Cargando, por favor espere...</span>
      </div>
    )
  }

  // Una vez que isClient es true, renderiza los children.
  return <>{memoizedChildren}</>
})

LoadingWrapper.displayName = 'LoadingWrapper'
export default LoadingWrapper
